import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  TrendingUp,
  LineChart,
  Gauge,
  Sparkles,
  Calculator,
  Clock,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SidebPill } from "@/components/SidebPill";
import { ProofTldr, MethodologyNote } from "@/components/ProofTldr";
import CaseStudyFaq from "@/components/CaseStudyFaq";
import { openChat } from "@/lib/chat";
import { useHashScroll } from "@/hooks/useHashScroll";
import { Seo, articleSchema, faqSchema, breadcrumbSchema } from "@/components/Seo";

const FAQS = [
  { q: "What is an AI Operating Partner and how is it different from hiring a VP of AI?", a: "An AI Operating Partner is a senior operator who plugs into your business to scope, enable, and optimize AI inside revenue, finance, or operations — without the cost, lock-in, or hiring risk of a permanent VP. SideB runs 4–6 week sprints (longer for complex rollouts) covering AI-stack review, model and tooling selection on enterprise tiers with contractually zero-training terms, playbook authoring, change-management, and an ROI dashboard the leadership team can read." },
  { q: "What cost savings did AI rationalization unlock in this case study?", a: "Collapsing five overlapping BI tools into a single AI-driven source of truth eliminated $100K in annual tool spend and reclaimed 30+ analyst hours per month — without losing any of the reporting capability the team relied on. Specific savings on any engagement depend on the size of the existing tool stack and the depth of redundancy." },
  { q: "Will SideB recommend specific AI vendors or stay neutral?", a: "Vendor-neutral by default. We surface a shortlist that fits your scope, data-residency, and risk posture — then run a structured selection with the client. Where the engagement scope includes deeper sourcing (e.g., enterprise LLM API contracts), we co-pilot the negotiation. We're never on a vendor's payroll." },
  { q: "How do you handle data privacy and model training concerns?", a: "Every model and tool we recommend operates on enterprise tiers with contractually zero-training terms — meaning your data is never used to retrain the vendor's models. We also build the policy layer (data classification, prompt hygiene, redaction rules) so AI is deployed safely from day one, not patched in retroactively." },
  { q: "How is ROI from an AI Operating Partner engagement measured?", a: "Every engagement ships with an ROI dashboard the leadership team can read — covering tool-spend eliminated, analyst time reclaimed, decisions accelerated, and any workflow-level KPIs scoped at activation. The dashboard makes the trajectory measurable rather than anecdotal. Pace and magnitude of ROI vary by scope, starting baseline, and the client team's pace of adoption." },
];

const KPIS = [
  { value: "$100K", label: "Annual BI tool spend eliminated", icon: TrendingUp },
  { value: "1", label: "Single source of truth across sub-accounts", icon: LineChart },
  { value: "30+ hrs", label: "Reclaimed per month across finance & product", icon: Clock },
  { value: "Real-time", label: "Replaced lagging monthly margin reports", icon: Zap },
];

export default function ProofAiops() {
  useHashScroll();

  return (
    <div className="bg-white" data-testid="proof-aiops-page">
      <Seo
        title="How SideB helped a growth-stage PSP eliminate $100K in BI tool spend | SideB Case Study"
        description="Case study: SideB embedded as an AI Operating Partner with a growth-stage Payment Service Provider — collapsing 5 overlapping BI tools into a single source of truth, eliminating $100K in annual tool spend and surfacing real-time sub-account margin."
        canonical="https://sideb.io/proof/aiops"
        ogType="article"
        keywords="AI Operating Partner, AIOps consulting, BI tool consolidation, reduce SaaS bloat with AI, PSP margin analytics"
        schema={[
          articleSchema({
            headline: "How SideB helped a growth-stage PSP eliminate $100K in BI tool spend",
            description: "SideB embedded as an AI Operating Partner — 5 BI tools collapsed to one source of truth, $100K annual spend eliminated, 30+ analyst hours/month reclaimed.",
            slug: "proof/aiops",
            datePublished: "2026-01-01",
            section: "Case Study",
          }),
          faqSchema(FAQS),
          breadcrumbSchema([
            { name: "Home", url: "https://sideb.io/" },
            { name: "Proof", url: "https://sideb.io/proof" },
            { name: "AIOps Case Study", url: "https://sideb.io/proof/aiops" },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0a1628] text-white">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          aria-hidden
          style={{
            background:
              "radial-gradient(60% 50% at 25% 30%, rgba(172,98,234,0.45) 0%, rgba(11,30,90,0) 70%)",
            filter: "blur(40px)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <Link
            to="/proof"
            className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white tracking-widest uppercase mb-6"
            data-testid="proof-aiops-back-to-hub"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All case studies
          </Link>
          <div className="label-mono text-white/70">Proof · AI Operating Partner · AI rollout · BI rationalization</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 tracking-[-0.035em] leading-[1.05] font-semibold max-w-4xl">
            One dashboard to replace the BI sprawl. <span className="text-[#c9a3f4]">$100K out of run-cost.</span> Sub-account margin, finally visible.
          </h1>
          <p className="text-lg text-white/80 mt-6 max-w-2xl leading-relaxed">
            How a growth-stage Payment Service Provider collapsed a fragmented BI stack into a single source of truth — eliminating <b>$100K in annual tool spend</b> and surfacing the true platform economics of every sub-account it processes.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3" data-testid="proof-aiops-hero-ctas">
            <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-11 px-6">
              <Link to="/services/aiops-enablement" data-testid="proof-aiops-cta-service">
                Explore AI Operating Partner <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="btn-pill h-11 px-6 border-white/30 text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="/inquiry" data-testid="proof-aiops-cta-brief">Send a brief</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* KPI grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 -mt-12 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {KPIS.map((k) => {
            const Icon = k.icon;
            return (
              <div
                key={k.label}
                className="card-sharp p-5 bg-white shadow-[0_18px_40px_-12px_rgba(11,30,90,0.18)]"
                data-testid={`proof-aiops-kpi-${k.label.toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
              >
                <div className="w-9 h-9 rounded-lg bg-[#ac62ea]/12 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-[#ac62ea]" />
                </div>
                <div className="font-display text-3xl mt-3 font-semibold tracking-tight">{k.value}</div>
                <div className="text-xs text-muted-foreground mt-1 leading-snug">{k.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* TL;DR */}
      <ProofTldr
        testid="proof-aiops-tldr"
        problem="Growth-stage PSP running five overlapping BI tools with no consolidated view of sub-account margin."
        approach="Embedded as the AI operating partner — designed and deployed a vendor-neutral single source of truth, then rationalized the BI stack on a planned schedule."
        outcome="$100K in annual tool spend eliminated, real-time sub-account margin visibility, ~30+ analyst hours reclaimed each month."
      />

      {/* Body sections */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 space-y-14">
        {/* Executive Summary */}
        <section data-testid="proof-aiops-summary">
          <div className="label-mono">Executive summary</div>
          <h2 className="font-display text-3xl mt-2 tracking-tight font-semibold">How SideB helped a growth-stage PSP eliminate $100K of annual BI tool spend with a single source of truth.</h2>
          <p className="mt-5 text-foreground/85 leading-relaxed text-lg">
            A growth-stage Payment Service Provider had outgrown a fragmented Business Intelligence stack — multiple vendors, overlapping pipelines, and no consolidated view of true platform cost per sub-account. <SidebPill /> was engaged under <b>AIOps Enablement</b> as the AI operating partner: senior expertise embedded into the team to design and deploy a centralized dashboard.
          </p>
          <p className="mt-4 text-foreground/85 leading-relaxed text-lg">
            The outcome was both <b>hard-dollar and operational</b>: <b>$100K eliminated in annual BI tool spend</b>, sub-account-level margin visibility for the first time, and a step-change in how fast the leadership team could act on platform economics.
          </p>
        </section>

        {/* Challenge */}
        <section id="challenge" className="scroll-mt-24" data-testid="proof-aiops-challenge">
          <div className="label-mono">The challenge</div>
          <h2 className="font-display text-3xl mt-2 tracking-tight font-semibold">A messy BI stack hiding the answer to &ldquo;which sub-accounts are actually profitable?&rdquo;</h2>
          <ul className="mt-5 space-y-3 text-foreground/85 leading-relaxed">
            <li className="flex gap-3"><Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" /> <span><b>Fragmented BI tooling.</b> Multiple overlapping BI platforms, dashboarding tools, and data-warehouse adjuncts — each licensed separately, each maintained by a different team, none telling a consistent story.</span></li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" /> <span><b>No sub-account margin visibility.</b> PSP economics live or die on the cost-to-serve of <i>every</i> sub-account — interchange, scheme fees, chargeback risk, support load. Those numbers existed across five systems and didn&apos;t reconcile.</span></li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" /> <span><b>Redundant tool spend.</b> Multiple vendors solving overlapping problems — paid for in full on annual contracts, with no clear owner to rationalize.</span></li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" /> <span><b>Lagging decisions.</b> Margin reviews ran on a monthly cadence at best — meaning by the time an unprofitable sub-account was flagged, another full cycle of losses had already booked.</span></li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" /> <span><b>Vendor lock-in risk.</b> The internal instinct was to consolidate by buying <i>another</i> all-in-one BI suite — re-creating the lock-in problem one layer up.</span></li>
          </ul>
        </section>

        {/* Solution */}
        <section id="solution" className="scroll-mt-24" data-testid="proof-aiops-solution">
          <div className="label-mono">The solution · AIOps Enablement</div>
          <h2 className="font-display text-3xl mt-2 tracking-tight font-semibold"><SidebPill /> embedded as the AI operating partner — designed and deployed a vendor-neutral single source of truth.</h2>
          <p className="mt-5 text-foreground/85 leading-relaxed text-lg">
            AIOps Enablement plugged senior AI capability into the org without the cost or lock-in of a permanent hire. Decisions stayed with the client; <SidebPill /> owned design, build velocity, and the rollout playbook.
          </p>
          <ol className="mt-6 space-y-4 text-foreground/85 leading-relaxed">
            <li className="flex gap-3"><span className="font-semibold text-[#ac62ea] flex-shrink-0">01</span> <span><b>BI stack & policy review.</b> Audited every active BI license, pipeline, and dashboard. Mapped overlap, identified the contracts driving the most spend without proportional value, and produced a rationalization plan.</span></li>
            <li className="flex gap-3"><span className="font-semibold text-[#ac62ea] flex-shrink-0">02</span> <span><b>Single-source-of-truth design.</b> Defined a vendor-neutral data model anchored on <i>sub-account</i> as the primary unit of economics — interchange, scheme fees, chargebacks, support cost, settlement timing, FX exposure.</span></li>
            <li className="flex gap-3"><span className="font-semibold text-[#ac62ea] flex-shrink-0">03</span> <span><b>Custom margin dashboard.</b> Deployed a centralized dashboard exposing true cost-to-serve per sub-account — replacing five tools the team used to flip between, surfaced in real-time instead of monthly.</span></li>
            <li className="flex gap-3"><span className="font-semibold text-[#ac62ea] flex-shrink-0">04</span> <span><b>Adoption & ROI playbook.</b> Enabled finance, product, and commercial teams on the new view, with rituals to act on it weekly — pricing, off-boarding, capacity, and risk decisions all anchored on the same numbers.</span></li>
            <li className="flex gap-3"><span className="font-semibold text-[#ac62ea] flex-shrink-0">05</span> <span><b>Tool consolidation & exit.</b> Decommissioned redundant BI vendors on a planned schedule — locking in <b>$100K of annual run-cost savings</b> without disrupting reporting continuity.</span></li>
          </ol>
        </section>

        {/* Extended value */}
        <section id="extended-value" className="scroll-mt-24" data-testid="proof-aiops-extended-value">
          <div className="label-mono">Quantifying extended value</div>
          <h2 className="font-display text-3xl mt-2 tracking-tight font-semibold">The $100K is the easy number. The compounding value is bigger.</h2>
          <p className="mt-5 text-foreground/85 leading-relaxed text-lg">
            Beyond the headline savings, three structural improvements re-shaped how the business runs.
          </p>

          <div className="mt-8 grid gap-4">
            <div className="card-sharp p-6 border-[#ac62ea]/30 bg-[#ac62ea]/[0.04]" data-testid="proof-aiops-value-time">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#ac62ea]/15 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-5 w-5 text-[#ac62ea]" />
                </div>
                <div>
                  <div className="label-mono">Operational efficiency</div>
                  <h3 className="font-display text-xl mt-1 font-semibold tracking-tight">~30+ hours reclaimed per month across finance & product</h3>
                  <p className="mt-3 text-foreground/85 leading-relaxed text-sm">
                    Manual reconciliation across five BI surfaces was a standing tax on the most senior analysts in the room. Conservatively: 2 finance analysts × 8 hrs/wk + 1 product lead × 4 hrs/wk = <b>~80 hrs/month</b> on reconciliation alone. The single source of truth cut that to <b>under 10 hrs/month</b> for spot-checks. At a blended fully-loaded rate of ~$95/hr, that&apos;s roughly <b>$80K of recovered analyst capacity per year</b> — redeployed into pricing strategy and risk work that actually moves margin.
                  </p>
                </div>
              </div>
            </div>

            <div className="card-sharp p-6 border-[#ac62ea]/30 bg-[#ac62ea]/[0.04]" data-testid="proof-aiops-value-margin">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#ac62ea]/15 flex items-center justify-center flex-shrink-0">
                  <Gauge className="h-5 w-5 text-[#ac62ea]" />
                </div>
                <div>
                  <div className="label-mono">Margin protection</div>
                  <h3 className="font-display text-xl mt-1 font-semibold tracking-tight">Mispriced sub-accounts surfaced and corrected — six figures of margin recovered</h3>
                  <p className="mt-3 text-foreground/85 leading-relaxed text-sm">
                    Once true cost-to-serve was visible per sub-account, the commercial team identified a meaningful share of accounts pricing below cost on a fully-loaded basis. Renegotiating, re-tiering, or off-boarding the structurally unprofitable tail produced an estimated <b>$120K–$180K of margin uplift annually</b> from full visibility — recurring, not one-off.
                  </p>
                </div>
              </div>
            </div>

            <div className="card-sharp p-6 border-[#ac62ea]/30 bg-[#ac62ea]/[0.04]" data-testid="proof-aiops-value-speed">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#ac62ea]/15 flex items-center justify-center flex-shrink-0">
                  <Zap className="h-5 w-5 text-[#ac62ea]" />
                </div>
                <div>
                  <div className="label-mono">Decision speed</div>
                  <h3 className="font-display text-xl mt-1 font-semibold tracking-tight">From lagging monthly reports to real-time agentic-commerce insight</h3>
                  <p className="mt-3 text-foreground/85 leading-relaxed text-sm">
                    Margin reviews shifted from a backwards-looking monthly ritual to a real-time operating loop. Pricing, risk, and capacity decisions now happen <b>within the same week</b> a signal appears — not 30–60 days later. That speed compounds: every cycle saved is one less full period of unprofitable volume booked, and one earlier window to capture upside on accounts trending the right way.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ROI Callout */}
        <section id="roi" className="scroll-mt-24" data-testid="proof-aiops-roi">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0a1628] via-[#0a1628] to-[#2a1a5e] text-white p-8 lg:p-12">
            <div
              className="absolute inset-0 pointer-events-none opacity-25"
              aria-hidden
              style={{
                background:
                  "radial-gradient(70% 60% at 80% 30%, rgba(172,98,234,0.55) 0%, rgba(11,30,90,0) 70%)",
                filter: "blur(40px)",
              }}
            />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/15 bg-white/[0.05]">
                <Calculator className="h-3.5 w-3.5 text-[#c9a3f4]" />
                <span className="text-[11px] text-white/85 font-medium uppercase tracking-widest">ROI callout</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl mt-5 tracking-[-0.03em] leading-[1.1] font-semibold">
                <span className="text-[#c9a3f4]">~10–12x</span> first-year return on a single AIOps Enablement engagement.
              </h2>

              <div className="mt-8 grid sm:grid-cols-3 gap-4">
                <div className="rounded-xl border border-white/15 bg-white/[0.04] p-5" data-testid="proof-aiops-roi-hard">
                  <div className="label-mono text-white/60">Hard savings</div>
                  <div className="font-display text-3xl mt-2 font-semibold tracking-tight">$100K</div>
                  <div className="text-xs text-white/65 mt-1 leading-snug">Annual BI tool spend eliminated</div>
                </div>
                <div className="rounded-xl border border-white/15 bg-white/[0.04] p-5" data-testid="proof-aiops-roi-extended">
                  <div className="label-mono text-white/60">Extended value</div>
                  <div className="font-display text-3xl mt-2 font-semibold tracking-tight">~$200K–$260K</div>
                  <div className="text-xs text-white/65 mt-1 leading-snug">Recovered analyst capacity + margin uplift, annualized</div>
                </div>
                <div className="rounded-xl border border-white/15 bg-white/[0.04] p-5" data-testid="proof-aiops-roi-investment">
                  <div className="label-mono text-white/60">Engagement investment</div>
                  <div className="font-display text-3xl mt-2 font-semibold tracking-tight">~$30K</div>
                  <div className="text-xs text-white/65 mt-1 leading-snug">Typical AIOps Enablement retainer, scoped to outcome</div>
                </div>
              </div>

              <div className="mt-8 rounded-xl border border-[#ac62ea]/40 bg-[#ac62ea]/[0.10] p-5">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-[#c9a3f4] flex-shrink-0 mt-0.5" />
                  <p className="text-white/90 leading-relaxed text-sm">
                    <b className="text-white">$300K–$360K of combined first-year value</b> against a <b className="text-white">~$30K</b> engagement — a conservative <b className="text-white">10–12x return in year one</b>, before factoring in the compounding decision-speed benefit. The dashboard, the data model, and the rituals stay with the client.
                  </p>
                </div>
              </div>

              <MethodologyNote testid="proof-aiops-methodology">
                Figures based on the client&apos;s reported $100K hard savings + <SidebPill variant="dark" className="!text-[0.9em]" />-modeled extended value using blended analyst rates and the share of mispriced sub-account volume the dashboard surfaced. Margin-uplift range reflects renegotiation vs. off-boarding mix.
              </MethodologyNote>
            </div>
          </div>
        </section>

        {/* Final callout */}
        <section id="next" className="scroll-mt-24" data-testid="proof-aiops-cta-section">
          <div className="rounded-2xl border border-border bg-white p-8 lg:p-10">
            <div className="label-mono">What this looks like for you</div>
            <h3 className="font-display text-2xl sm:text-3xl mt-2 tracking-tight font-semibold leading-snug">
              If your BI stack is sprawling and the answer to &ldquo;which line of business is actually profitable?&rdquo; lives across five tools — that&apos;s the engagement.
            </h3>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              AIOps Enablement plugs senior AI capability into your team as a retainer — no headcount commitment, no vendor lock-in, decisions stay with you.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button asChild className="btn-pill bg-[#ac62ea] hover:bg-[#8a48cf] text-white h-11 px-6" data-testid="proof-aiops-final-service">
                <Link to="/services/aiops-enablement">
                  Explore AI Operating Partner <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="btn-pill h-11 px-6" data-testid="proof-aiops-final-brief">
                <Link to="/inquiry">Send a custom brief</Link>
              </Button>
              <button
                type="button"
                onClick={() => openChat({ context: "Proof · AIOps case study · Custom inquiry" })}
                className="btn-pill h-11 px-6 rounded-full text-foreground/70 hover:text-foreground text-sm font-medium underline-offset-4 hover:underline"
                data-testid="proof-aiops-final-chat"
              >
                Or chat with us &rarr;
              </button>
            </div>
          </div>
        </section>

        <CaseStudyFaq testid="proof-aiops-faq" schemaId="aiops" items={FAQS} />

        {/* Cross-link */}
        <div className="text-center pt-4" data-testid="proof-aiops-cross-link">
          <Link
            to="/proof/studio-retainer"
            className="inline-flex items-center gap-1.5 text-sm text-[#ac62ea] hover:text-[#8a48cf] font-medium"
          >
            Next case study: Studio Retainer — fraud crisis to good standing &rarr;
          </Link>
        </div>
      </div>
    </div>
  );
}
